import { NodeMapValue } from '@common/prosemirror/model/node-find';
import { Node } from 'prosemirror-model';

export declare type IterateNodeCallback = (child: Node, data: any, pos: number, parent: Node, childIndex: number) => any;

/**
 * Loops through all the children of a node invoking a function for each child.
 * The callback function is given extra information compared to a normal loop through the children.
 * This information includes the document position of the child.
 * @param node The parent to loop through.
 * @param posOfNode The position of the parent node in the document.
 * @param iteratee The callback function to invoke for each child of node.
 */
export function forEachChild(node: Node, posOfNode: number, iteratee: (childNode: Node, parentOffset: number, docPos: number, childIndex: number) => void) {
  node.content.forEach((childNode, parentOffset, index) => {
    // Add one to the doc position to account for the parent node's position
    iteratee(childNode, parentOffset, 1 + posOfNode + parentOffset, index);
  });
}

/** Iterates over the child elements of the specified type (or all child elements if type is not specified) until the function f returns false.  */
export function iterateChildren(item: NodeMapValue, nodeTypeName: string, f: (child: NodeMapValue) => boolean | void): void {
  const node = item?.node;
  if (node?.childCount) {
    let offset = 0;
    for (let i = 0; i < node.childCount; i++) {
      const childNode = node.child(i);
      if (!nodeTypeName || childNode.type.name === nodeTypeName) {
        const childItem = new NodeMapValue(childNode, item.pos + 1 + offset, i, item.pos);
        const res = f(childItem);
        if (res === false)
          break;
      }
      offset += childNode.nodeSize;
    }
  }
}

/** Iterates over the child node of the specified type (or all child elements if type is not specified) until the function f returns false.  */
export function iterateChildNodes(node: Node, nodeTypeName: string, f: (child: Node) => boolean | void): void {
  const item = new NodeMapValue(node, 0, 0, undefined);
  iterateChildren(item, nodeTypeName, (child) => f(child.node));
}

// Iterates through the nodes between `from` and `to`. The returned value from iteratee is passed into the child calls to iteratee
// TODO: parent is always undefined
export function nodesBetweenWithData(node: Node, from: number, to: number, iteratee: IterateNodeCallback, data?: any, nodeStart: number = 0, parent: Node = null) {
  for (let i = 0, pos = 0; pos < to; i += 1) {
    const child = node.child(i);
    const end = pos + child.nodeSize;

    if (end > from) {
      const childData = iteratee(child, data, nodeStart + pos, parent, i);

      if (child.content.size) {
        const start = pos + 1;
        nodesBetweenWithData(child, Math.max(0, from - start), Math.min(child.content.size, to - start), iteratee, childData, nodeStart + start);
      }
    }

    pos = end;
  }
}
